'use client';

// ============================================================================
// OPERATIONS CONTEXT - Shared State for Service Operations Components
// Provides unified state management for jobs, vehicles, locations, and events
// Date: December 30, 2025
// ============================================================================

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import { apiService } from '@/lib/apiService';
import { networkState } from '@/lib/networkState';
import { maintenanceState } from '@/lib/maintenanceState';
import { workerEvents } from '@/lib/workerEventBus';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import useGeolocation from '@/hooks/useGeolocation';
import { respondToLocationCaptureRequest } from '@/lib/locationCaptureRequest';
import { normalizeSolutionItemTypes } from '@/lib/operations/itemTypes';
import { setActiveSolutionKeyMirror } from '@/lib/operations/activeSolutionKeyStore';
import {
  buildOperationsRealtimeEvent,
  normalizeOperationsRealtimeMessage,
} from '@/lib/websocket/operationsRealtime';
import {
  getPreferredSmartTaskSolution,
  getSmartTaskSolutionManifest,
  savePreferredSmartTaskSolution,
  saveSmartTaskSolutionManifest,
} from '@/lib/operations/smartTaskOfflineCache';
import masterSync from '@/lib/masterSync';
import { IAM_PERMISSIONS_CHANGED_EVENT } from '@/lib/iam/useEffectivePermissions';
import { useSession } from '@/components/SessionProvider';
import { isPortalSessionLocked } from '@/lib/sessionLockState';
import type {
  DispatchedRoute as SharedDispatchedRoute,
  DispatchedRouteStop as SharedDispatchedRouteStop,
} from '@/types/routes';

// Re-export shared route types for backward compatibility
export type { RouteContext } from '@/types/routes';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Returns true for AbortError — which happens when a request times out or is
 * cancelled by an AbortController. These are expected during polling/refresh
 * cycles and should not be logged as errors.
 */
function isAbortError(err: unknown): boolean {
  if (err instanceof Error) {
    return err.name === 'AbortError' || err.message.includes('aborted');
  }
  return false;
}

function toFleetVehicle(vehicle: Record<string, unknown>): FleetVehicle | null {
  const id = vehicle?.id ?? vehicle?.external_id ?? vehicle?.vehicle_id;
  if (id == null) return null;
  const str = (val: unknown): string | undefined =>
    val != null ? String(val) : undefined;
  const num = (val: unknown): number | undefined =>
    val != null && val !== '' ? Number(val) : undefined;
  const cf = (vehicle.custom_fields ?? {}) as Record<string, unknown>;
  return {
    id: String(id),
    registration: str(vehicle.title) || str(vehicle.registration) || str(vehicle.item_number) || 'Unknown',
    make: str(cf.make) || str(vehicle.make) || undefined,
    model: str(cf.model) || str(vehicle.model) || undefined,
    year: num(cf.year ?? vehicle.year),
    vin: str(cf.vin) || str(vehicle.vin) || undefined,
    odometer: num(cf.current_odometer ?? vehicle.current_odometer),
    fuelLevel: num(cf.fuel_level ?? vehicle.fuelLevel),
    status: str(vehicle.status) || undefined,
    assigned_driver_id: num(vehicle.assigned_to),
    assigned_driver_name: str(vehicle.assigned_to_name) || undefined,
  };
}

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface WorkItem {
  id: string;
  item_number: string;
  item_type: string;
  item_subtype?: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  assigned_to: number | null;
  assigned_to_name?: string;
  contact_id?: number;
  contact_name?: string;
  contact_phone?: string;
  location_address?: string;
  location_coordinates?: { lat: number; lng: number } | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  estimated_duration_minutes?: number;
  custom_fields?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface FleetVehicle {
  id: string;
  registration: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  odometer?: number;
  fuelLevel?: number;
  status?: string;
  assigned_driver_id?: number;
  assigned_driver_name?: string;
  // Live tracking from Cartrack
  live?: {
    latitude: number;
    longitude: number;
    speed: number;
    ignition: boolean;
    idling: boolean;
    heading?: number;
    last_updated: string;
  };
}

export interface TechnicianLocation {
  user_id: number;
  user_name: string;
  user_email?: string;
  avatar_url?: string;
  latitude: number;
  longitude: number;
  accuracy?: number;
  heading?: number;
  speed?: number;
  source: string; // 'app' | 'checkin' | 'gps'
  last_updated_at: string;
  is_stale: boolean;
  current_job?: {
    id: string;
    item_number: string;
    title: string;
    status: string;
  } | null;
}

export interface ScheduledAppointment {
  id: string;
  item_number: string;
  title: string;
  status: string;
  scheduled_start: string;
  scheduled_end?: string;
  estimated_duration_minutes: number;
  contact_name?: string;
  contact_phone?: string;
  location_address?: string;
  location_coordinates?: { lat: number; lng: number } | null;
  assigned_to?: number;
  job_id?: string; // Parent job reference
}

// RouteStop and DispatchedRoute now use the shared unified types from @/types/routes.
// These aliases maintain backward compatibility for existing consumers.
export type RouteStop = SharedDispatchedRouteStop;
export type DispatchedRoute = SharedDispatchedRoute;

// Event types for cross-component communication
export type OperationsEventType =
  | 'job:status_changed'
  | 'job:checked_in'
  | 'job:checked_out'
  | 'job:travel_started'
  | 'job:arrived'
  | 'route:dispatched'
  | 'route:updated'
  | 'vehicle:selected'
  | 'vehicle:location_updated'
  | 'location:updated'
  | 'equipment:checked_out'
  | 'equipment:checked_in'
  | 'appointment:created'
  | 'appointment:updated'
  | 'appointments:batch_updated'
  | 'route:eta_writeback'
  | 'schedule:generate_routes';

export interface OperationsEvent {
  type: OperationsEventType;
  payload: Record<string, unknown>;
  timestamp: string;
  source: string; // Component that triggered the event
}

type EventCallback = (event: OperationsEvent) => void;

// ============================================================================
// Context Interface
// ============================================================================

export interface SolutionTeam {
  teamId: string;
  solutionKey: string;
  solutionName: string;
  icon: string;
  color: string;
  role: string;
  featureOverrides: Record<string, unknown> | null;
  featurePolicies: Record<string, unknown> | null;
  featurePoliciesByRoleScope?: Record<string, Record<string, unknown>> | null;
  settings: Record<string, unknown> | null;
  itemTypes: string[];
  dashboardKey: string | null;
  memberCount: number;
}

interface SolutionApiItem {
  team_id?: string;
  solution_key?: string;
  solution_name?: string;
  icon?: string;
  color?: string;
  role?: string;
  feature_overrides?: unknown;
  feature_policies?: unknown;
  feature_policies_by_role_scope?: unknown;
  settings?: unknown;
  item_types?: unknown;
  dashboard_key?: string | null;
  member_count?: number;
}

interface AppointmentApiItem {
  id: string;
  item_number: string;
  title: string;
  status: string;
  scheduled_start: string;
  scheduled_end?: string;
  estimated_duration_minutes?: number;
  contact_name?: string;
  contact_phone?: string;
  location_address?: string;
  location_coordinates?: { lat: number; lng: number } | null;
  assigned_to?: number;
  parent_id?: string;
}

interface OperationsContextType {
  // Shared state
  jobs: WorkItem[];
  todayAppointments: ScheduledAppointment[];
  vehicles: FleetVehicle[];
  technicianLocations: TechnicianLocation[];
  dispatchedRoutes: DispatchedRoute[];
  
  // Loading states
  isLoadingJobs: boolean;
  isLoadingVehicles: boolean;
  isLoadingLocations: boolean;
  
  // WebSocket connection
  isConnected: boolean;
  
  // Actions
  refreshJobs: () => Promise<void>;
  refreshVehicles: () => Promise<void>;
  refreshLocations: () => Promise<void>;
  refreshAll: () => Promise<void>;
  
  // Job actions
  updateJobStatus: (jobId: string, status: string) => Promise<void>;
  startTravelToJob: (jobId: string, vehicleId?: string) => void;
  checkInToJob: (jobId: string, location?: { lat: number; lng: number }) => void;
  checkOutFromJob: (jobId: string) => void;
  
  // Route dispatch
  dispatchRoute: (route: DispatchedRoute) => void;
  getDispatchedRouteForUser: (userId: number) => DispatchedRoute | null;
  
  // Vehicle actions
  selectVehicle: (vehicleId: string) => void;
  selectedVehicleId: string | null;
  getVehicleById: (vehicleId: string) => FleetVehicle | null;
  
  // Event system
  emitEvent: (type: OperationsEventType, payload: Record<string, unknown>, source: string) => void;
  onEvent: (type: OperationsEventType | OperationsEventType[], callback: EventCallback) => () => void;
  
  // Last refresh timestamps
  lastJobsRefresh: Date | null;
  lastVehiclesRefresh: Date | null;
  lastLocationsRefresh: Date | null;

  // Solution/team scoping
  activeSolutionKey: string | null;
  activeSolutionTeam: SolutionTeam | null;
  userSolutionTeams: SolutionTeam[];
  setActiveSolution: (solutionKey: string) => void;
  isMultiSolution: boolean;
  isLoadingSolutions: boolean;
  refreshSolutionTeams: () => Promise<void>;
}

const OperationsContext = createContext<OperationsContextType | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

export function OperationsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const session = useSession();
  // Session active client (JWT client_id) — user.client_id is 0 for
  // root/multi-client users and would disable the realtime channel below.
  const clientId = authService.getSessionClientId() ?? Number(user?.client_id || 0);
  const userId = Number(user?.id || 0);
  const isSessionLocked = session.sessionState.isLocked || isPortalSessionLocked();
  
  // State
  const [jobs, setJobs] = useState<WorkItem[]>([]);
  const [todayAppointments, setTodayAppointments] = useState<ScheduledAppointment[]>([]);
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [technicianLocations, setTechnicianLocations] = useState<TechnicianLocation[]>([]);
  const [dispatchedRoutes, setDispatchedRoutes] = useState<DispatchedRoute[]>([]);
  
  // Loading states
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  
  // Selected state
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  
  // Timestamps
  const [lastJobsRefresh, setLastJobsRefresh] = useState<Date | null>(null);
  const [lastVehiclesRefresh, setLastVehiclesRefresh] = useState<Date | null>(null);
  const [lastLocationsRefresh, setLastLocationsRefresh] = useState<Date | null>(null);
  
  // Event listeners
  const eventListenersRef = useRef<Map<OperationsEventType, Set<EventCallback>>>(new Map());

  // Ref for refreshJobs to avoid use-before-define issues with WebSocket callback
  const refreshJobsRef = useRef<(() => Promise<void>) | null>(null);

  // Solution/team scoping
  const [userSolutionTeams, setUserSolutionTeams] = useState<SolutionTeam[]>([]);
  const [activeSolutionKey, setActiveSolutionKey] = useState<string | null>(null);
  const [isLoadingSolutions, setIsLoadingSolutions] = useState(false);
  const solutionTeamsRequestIdRef = useRef(0);

  const activeSolutionTeam = useMemo(
    () => userSolutionTeams.find(t => t.solutionKey === activeSolutionKey) || null,
    [userSolutionTeams, activeSolutionKey]
  );
  const isMultiSolution = userSolutionTeams.length > 1;

  // Mirror the active solution into the lightweight store so hooks that
  // cannot import this context (workflowActionPermissions) can still apply
  // the per-solution permission slice.
  useEffect(() => {
    setActiveSolutionKeyMirror(activeSolutionKey);
  }, [activeSolutionKey]);
  // Passive geolocation handle for admin 'location_capture_requested'
  // events — no auto-prompt or watcher here; permission is requested inside
  // respondToLocationCaptureRequest when a request actually arrives.
  const {
    requestPermission: requestGpsPermission,
    getCurrentPosition: getGpsPosition,
  } = useGeolocation({
    enableHighAccuracy: true,
    autoRequestPermission: false,
    monitorPermission: false,
    watchPosition: false,
  });
  const locationCaptureDepsRef = useRef({ currentUserId: userId, requestGpsPermission, getGpsPosition });
  locationCaptureDepsRef.current = { currentUserId: userId, requestGpsPermission, getGpsPosition };

  // Admin 'location_capture_requested' pushes (the wake-up escalation path)
  // arrive via capacitorDeviceService as a 'locationCaptureRequested' window
  // event — handle them through the same capture flow as realtime messages.
  // A push tap can also cold-start the app before this context mounts, so the
  // service persists the request; consume it here once on mount.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePushCaptureRequest = (event: Event) => {
      const detail = (event as CustomEvent<Record<string, unknown>>).detail;
      if (!isRecord(detail)) {
        return;
      }
      void respondToLocationCaptureRequest(
        { reason: 'location_capture_requested', payload: detail },
        locationCaptureDepsRef.current,
      );
    };

    window.addEventListener('locationCaptureRequested', handlePushCaptureRequest);

    try {
      const raw = sessionStorage.getItem('pending_location_capture')
        || localStorage.getItem('totalaccess_pending_location_capture');
      sessionStorage.removeItem('pending_location_capture');
      localStorage.removeItem('totalaccess_pending_location_capture');
      if (raw) {
        const pending = JSON.parse(raw) as Record<string, unknown>;
        const timestamp = typeof pending.timestamp === 'number' ? pending.timestamp : 0;
        // Ignore stale requests older than 15 minutes
        if (Date.now() - timestamp < 15 * 60 * 1000) {
          void respondToLocationCaptureRequest(
            { reason: 'location_capture_requested', payload: pending },
            locationCaptureDepsRef.current,
          );
        }
      }
    } catch {
      // Malformed persisted payload — nothing to consume
    }

    return () => window.removeEventListener('locationCaptureRequested', handlePushCaptureRequest);
  }, []);

  const operationsChannel = clientId > 0 ? `operations:client:${clientId}` : undefined;
  const applyOperationsRealtimeEvent = useCallback((rawData: unknown) => {
    const normalized = normalizeOperationsRealtimeMessage(rawData);
    if (!normalized) {
      return;
    }

    const eventType = normalized.event as OperationsEventType;
    const eventPayload = normalized.payload || {};

    console.log('[OperationsContext] Received operations event:', eventType, eventPayload);

    if (eventType === 'job:status_changed') {
      const jobId = typeof eventPayload.jobId === 'string'
        ? eventPayload.jobId
        : typeof eventPayload.job_id === 'string'
          ? eventPayload.job_id
          : null;
      const status = typeof eventPayload.status === 'string' ? eventPayload.status : null;

      if (jobId && status) {
        setJobs(prev => prev.map(job => job.id === jobId ? { ...job, status } : job));
      }
    } else if (eventType === 'route:dispatched') {
      const route = eventPayload as unknown as DispatchedRoute;
      if (route?.technician_id) {
        setDispatchedRoutes(prev => {
          const existing = prev.findIndex(r => r.technician_id === route.technician_id);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = route;
            return updated;
          }
          return [...prev, route];
        });
      }
    } else if (eventType === 'location:updated') {
      const locationPayload = eventPayload as Partial<TechnicianLocation> & { user_id?: number };
      if (locationPayload.user_id) {
        setTechnicianLocations(prev => {
          const existing = prev.findIndex(location => location.user_id === locationPayload.user_id);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = { ...updated[existing], ...locationPayload } as TechnicianLocation;
            return updated;
          }
          return [...prev, locationPayload as TechnicianLocation];
        });
      }
    }

    const listeners = eventListenersRef.current.get(eventType);
    if (listeners) {
      const event: OperationsEvent = {
        type: eventType,
        payload: eventPayload,
        timestamp: typeof normalized.timestamp === 'number'
          ? new Date(normalized.timestamp).toISOString()
          : (normalized.timestamp || new Date().toISOString()),
        source: normalized.actor_user_id ? `user:${normalized.actor_user_id}` : (normalized.source || 'websocket'),
      };
      listeners.forEach(callback => {
        try {
          callback(event);
        } catch (err) {
          console.error('[OperationsContext] Event listener error:', err);
        }
      });
    }
  }, []);

  const handleOperationsRealtimeMessage = useCallback((data: unknown) => {
    applyOperationsRealtimeEvent(data);

    // Admin 'location_capture_requested' is also published on the
    // operations channel — handle it here so it works on any foregrounded
    // screen, not only while SmartTaskShell is mounted. Deduped by
    // request_id against the SmartTask/tasks-channel copy.
    if (isRecord(data)) {
      void respondToLocationCaptureRequest(data, locationCaptureDepsRef.current);
    }

    const raw = isRecord(data) ? data : {};
    if (raw.type === 'location_update') {
      setTechnicianLocations(prev => {
        const existing = prev.findIndex(location => location.user_id === raw.user_id);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { ...updated[existing], ...(raw as Partial<TechnicianLocation>) };
          return updated;
        }
        return [...prev, raw as unknown as TechnicianLocation];
      });
    } else if (raw.type === 'job_update') {
      // Use ref to avoid use-before-define issue
      void refreshJobsRef.current?.();
    }
  }, [applyOperationsRealtimeEvent]);

  const operationsChannelOptions = useMemo(() => ({
    channel: operationsChannel,
    autoConnect: isAuthenticated && clientId > 0,
    retainLatestData: false,
    retainMessages: false,
    onMessage: handleOperationsRealtimeMessage,
  }), [operationsChannel, isAuthenticated, clientId, handleOperationsRealtimeMessage]);

  const {
    isConnected,
    publish: publishRealtime,
  } = useWebSocketChannel(operationsChannelOptions);

  const setActiveSolution = useCallback((solutionKey: string) => {
    setActiveSolutionKey(solutionKey);
    try { localStorage.setItem('ta_active_solution', solutionKey); } catch {}
    if (clientId > 0 && userId > 0) {
      void savePreferredSmartTaskSolution(clientId, userId, solutionKey);
    }
  }, [clientId, userId]);

  const refreshSolutionTeams = useCallback(async () => {
    if (!isAuthenticated || !clientId || !userId) {
      setUserSolutionTeams([]);
      setActiveSolutionKey(null);
      setIsLoadingSolutions(false);
      return;
    }

    const requestId = ++solutionTeamsRequestIdRef.current;
    setIsLoadingSolutions(true);

    const parseJsonField = <T extends Record<string, unknown> = Record<string, unknown>>(value: unknown): T | null => {
      if (!value) return null;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return isRecord(parsed) ? (parsed as T) : null;
        } catch {
          return null;
        }
      }
      return isRecord(value) ? (value as T) : null;
    };

    const hydrateFromCachedManifest = async (): Promise<boolean> => {
      const cachedManifest = await getSmartTaskSolutionManifest(clientId, userId);
      if (requestId !== solutionTeamsRequestIdRef.current || !cachedManifest) return false;

      const cachedTeams = Array.isArray(cachedManifest.teams) ? cachedManifest.teams : [];
      setUserSolutionTeams(cachedTeams);

      let preferred = await getPreferredSmartTaskSolution(clientId, userId);
      if (!preferred) {
        try { preferred = localStorage.getItem('ta_active_solution'); } catch {}
      }

      const valid = cachedTeams.find((team) => team.solutionKey === preferred);
      const nextActiveSolutionKey = valid?.solutionKey
        || cachedManifest.activeSolutionKey
        || cachedTeams[0]?.solutionKey
        || null;

      setActiveSolutionKey(nextActiveSolutionKey);
      return cachedTeams.length > 0;
    };

    try {
      const tokens = authService.getTokens();
      const shouldUseOfflineCache = !tokens?.access_token || !networkState.isOnline();
      if (shouldUseOfflineCache) {
        await hydrateFromCachedManifest();
        return;
      }

      const res = await fetch('/api/v1/operations/my-solutions', {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });

      if (!res.ok) {
        await hydrateFromCachedManifest();
        return;
      }

      const data = await res.json();
      if (requestId !== solutionTeamsRequestIdRef.current || !data.success) {
        await hydrateFromCachedManifest();
        return;
      }

      const solutionItems: SolutionApiItem[] = Array.isArray(data.solutions) ? data.solutions : [];
      const teams: SolutionTeam[] = solutionItems.map((s) => ({
        teamId: s.team_id || '',
        solutionKey: s.solution_key || '',
        solutionName: s.solution_name || '',
        icon: s.icon || 'Sparkles',
        color: s.color || '#3b82f6',
        role: s.role || 'user',
        featureOverrides: parseJsonField(s.feature_overrides),
        featurePolicies: parseJsonField(s.feature_policies),
        featurePoliciesByRoleScope: parseJsonField<Record<string, Record<string, unknown>>>(s.feature_policies_by_role_scope),
        settings: parseJsonField(s.settings),
        itemTypes: normalizeSolutionItemTypes(s.item_types),
        dashboardKey: s.dashboard_key || null,
        memberCount: s.member_count || 0,
      }));

      if (requestId !== solutionTeamsRequestIdRef.current) return;

      setUserSolutionTeams(teams);

      let preferred = await getPreferredSmartTaskSolution(clientId, userId);
      if (!preferred) {
        try { preferred = localStorage.getItem('ta_active_solution'); } catch {}
      }

      const valid = teams.find((team) => team.solutionKey === preferred);
      const nextActiveSolutionKey = valid?.solutionKey || teams[0]?.solutionKey || null;
      setActiveSolutionKey(nextActiveSolutionKey);

      await saveSmartTaskSolutionManifest({
        clientId,
        userId,
        teams,
        activeSolutionKey: nextActiveSolutionKey,
      });
    } catch (err) {
      console.error('[OperationsContext] Failed to load solution teams:', err);
      await hydrateFromCachedManifest();
    } finally {
      if (requestId === solutionTeamsRequestIdRef.current) {
        setIsLoadingSolutions(false);
      }
    }
  }, [clientId, isAuthenticated, userId]);

  useEffect(() => {
    void refreshSolutionTeams();

    return () => {
      solutionTeamsRequestIdRef.current += 1;
    };
  }, [refreshSolutionTeams]);

  // Re-fetch solution access when the server pushes an IAM permission change
  // over the WebSocket channel — solution activation/deactivation and group
  // membership changes alter which solutions my-solutions returns.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => { void refreshSolutionTeams(); };
    window.addEventListener(IAM_PERMISSIONS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(IAM_PERMISSIONS_CHANGED_EVENT, handler);
  }, [refreshSolutionTeams]);
  
  // ============================================================================
  // Event System
  // ============================================================================
  
  const emitEvent = useCallback((type: OperationsEventType, payload: Record<string, unknown>, source: string) => {
    const event: OperationsEvent = {
      type,
      payload,
      timestamp: new Date().toISOString(),
      source
    };
    
    console.log('[OperationsContext] Emitting event:', type, payload);
    
    // Notify local listeners
    const listeners = eventListenersRef.current.get(type);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(event);
        } catch (err) {
          console.error('[OperationsContext] Event listener error:', err);
        }
      });
    }
    
    // Also dispatch as window event for cross-module communication
    if (typeof window !== 'undefined') {
      const detailPayload = isRecord(payload) ? payload : { payload };
      window.dispatchEvent(new CustomEvent(type, { 
        detail: { ...detailPayload, timestamp: event.timestamp, source } 
      }));
    }
    
    if (operationsChannel && isAuthenticated && networkState.isOnline()) {
      void publishRealtime(
        operationsChannel,
        buildOperationsRealtimeEvent({
          eventType: type,
          clientId,
          source: source || 'OperationsContext',
          sourceModule: 'operations_context',
          actorUserId: userId || null,
          payload,
        })
      ).catch((error) => {
        console.error('[OperationsContext] Failed to publish operations realtime event:', error);
      });
    }
  }, [clientId, isAuthenticated, operationsChannel, publishRealtime, userId]);
  
  const onEvent = useCallback((
    types: OperationsEventType | OperationsEventType[], 
    callback: EventCallback
  ): (() => void) => {
    const typeArray = Array.isArray(types) ? types : [types];
    
    typeArray.forEach(type => {
      if (!eventListenersRef.current.has(type)) {
        eventListenersRef.current.set(type, new Set());
      }
      eventListenersRef.current.get(type)!.add(callback);
    });
    
    // Return unsubscribe function
    return () => {
      typeArray.forEach(type => {
        eventListenersRef.current.get(type)?.delete(callback);
      });
    };
  }, []);
  
  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  const refreshJobs = useCallback(async () => {
    if (!isAuthenticated) return;
    if (maintenanceState.isInMaintenance) return;
    if (isSessionLocked) return;
    
    setIsLoadingJobs(true);
    try {
      if (!networkState.isOnline()) {
        const cached = await masterSync.getCachedOperationsWorkItems({ clientId }).catch(() => []);
        setJobs(cached.filter((item: Record<string, unknown>) => (item as { item_type?: string }).item_type === 'job') as WorkItem[]);
        setLastJobsRefresh(new Date());
        return;
      }

      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      // Fetch jobs assigned to current user or all jobs if admin
      const response = await fetch('/api/v1/work-items?item_type=job&status=open,in_progress,pending', {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.items || data.data || []);
        setLastJobsRefresh(new Date());
      }
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('[OperationsContext] Failed to refresh jobs:', err);
      }
    } finally {
      setIsLoadingJobs(false);
    }
  }, [isAuthenticated, isSessionLocked, clientId]);

  // Sync refreshJobs with ref for use in WebSocket callback
  useEffect(() => {
    refreshJobsRef.current = refreshJobs;
  }, [refreshJobs]);

  const refreshTodayAppointments = useCallback(async () => {
    if (!isAuthenticated) return;
    if (maintenanceState.isInMaintenance) return;
    if (isSessionLocked) return;
    
    try {
      if (!networkState.isOnline()) {
        const cached = await masterSync.getCachedOperationsWorkItems({ clientId }).catch(() => []);
        const today = new Date().toISOString().split('T')[0];
        setTodayAppointments(
          cached
            .filter((item: Record<string, unknown>) => {
              const typed = item as { item_type?: string; scheduled_start?: string };
              return typed.item_type === 'appointment' && typed.scheduled_start?.startsWith(today);
            })
            .map((item: Record<string, unknown>) => {
              const i = item as Record<string, unknown> & {
                id: string;
                item_number: string;
                title: string;
                status: string;
                scheduled_start?: string;
                scheduled_end?: string;
                estimated_duration_minutes?: number;
                contact_name?: string;
                contact_phone?: string;
                location_address?: string;
                location_coordinates?: { lat: number; lng: number } | null;
                assigned_to?: number;
                parent_id?: string;
              };
              return {
                id: i.id,
                item_number: i.item_number,
                title: i.title,
                status: i.status,
                scheduled_start: i.scheduled_start || today,
                scheduled_end: i.scheduled_end,
                estimated_duration_minutes: i.estimated_duration_minutes || 60,
                contact_name: i.contact_name,
                contact_phone: i.contact_phone,
                location_address: i.location_address,
                location_coordinates: i.location_coordinates,
                assigned_to: i.assigned_to,
                job_id: i.parent_id,
              };
            }),
        );
        return;
      }

      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(
        `/api/v1/work-items?item_type=appointment&scheduled_start_date=${today}`, 
        { headers: { 'Authorization': `Bearer ${tokens.access_token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const appointmentItems: AppointmentApiItem[] = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.data)
            ? data.data
            : [];
        const appointments = appointmentItems.map((item) => ({
          id: item.id,
          item_number: item.item_number,
          title: item.title,
          status: item.status,
          scheduled_start: item.scheduled_start,
          scheduled_end: item.scheduled_end,
          estimated_duration_minutes: item.estimated_duration_minutes || 60,
          contact_name: item.contact_name,
          contact_phone: item.contact_phone,
          location_address: item.location_address,
          location_coordinates: item.location_coordinates,
          assigned_to: item.assigned_to,
          job_id: item.parent_id
        }));
        setTodayAppointments(appointments);
      }
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('[OperationsContext] Failed to refresh appointments:', err);
      }
    }
  }, [isAuthenticated, isSessionLocked, clientId]);
  
  const refreshVehicles = useCallback(async () => {
    if (!isAuthenticated) return;
    if (maintenanceState.isInMaintenance) return;
    if (isSessionLocked) return;
    
    setIsLoadingVehicles(true);
    try {
      if (!networkState.isOnline()) {
        const cached = await masterSync.getCachedFleetVehicles({ clientId }).catch(() => []);
        setVehicles(cached.map((v: Record<string, unknown>) => toFleetVehicle(v)).filter((v: FleetVehicle | null): v is FleetVehicle => v !== null));
        setLastVehiclesRefresh(new Date());
        return;
      }

      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const response = await fetch('/api/v1/work-items/fleet', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getVehicles' })
      });
      
      if (response.ok) {
        const data = await response.json();
        const rawVehicles = Array.isArray(data?.data?.vehicles)
          ? data.data.vehicles
          : Array.isArray(data?.vehicles)
            ? data.vehicles
            : [];
        void masterSync.cacheFleetVehicles(rawVehicles, clientId).catch(() => undefined);
        setVehicles(rawVehicles.map((v: Record<string, unknown>) => toFleetVehicle(v)).filter((v: FleetVehicle | null): v is FleetVehicle => v !== null));
        setLastVehiclesRefresh(new Date());
      }
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('[OperationsContext] Failed to refresh vehicles:', err);
      }
    } finally {
      setIsLoadingVehicles(false);
    }
  }, [isAuthenticated, isSessionLocked, clientId]);
  
  const refreshLocations = useCallback(async () => {
    if (!isAuthenticated) return;
    if (maintenanceState.isInMaintenance) return; // Skip during maintenance — DB is down
    if (isSessionLocked) return;
    if (!networkState.isOnline()) return;
    
    setIsLoadingLocations(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      // Use apiService for automatic 401 → token refresh → retry
      const data = await apiService.get<{ field_workers?: TechnicianLocation[] }>('/api/v1/operations/live-map', { silent: true });
      setTechnicianLocations(data.field_workers || []);
      setLastLocationsRefresh(new Date());
    } catch (err) {
      if (!isAbortError(err)) {
        console.error('[OperationsContext] Failed to refresh locations:', err);
      }
    } finally {
      setIsLoadingLocations(false);
    }
  }, [isAuthenticated, isSessionLocked]);
  
  const refreshAll = useCallback(async () => {
    await Promise.all([
      refreshJobs(),
      refreshTodayAppointments(),
      refreshVehicles(),
      refreshLocations()
    ]);
  }, [refreshJobs, refreshTodayAppointments, refreshVehicles, refreshLocations]);
  
  // ============================================================================
  // Job Actions
  // ============================================================================
  
  const updateJobStatus = useCallback(async (jobId: string, status: string) => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;
      
      const response = await fetch(`/api/v1/work-items/${jobId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        // Update local state
        setJobs(prev => prev.map(job => 
          job.id === jobId ? { ...job, status } : job
        ));
        
        // Emit event
        emitEvent('job:status_changed', { jobId, status }, 'OperationsContext');
      }
    } catch (err) {
      console.error('[OperationsContext] Failed to update job status:', err);
    }
  }, [emitEvent]);
  
  const startTravelToJob = useCallback((jobId: string, vehicleId?: string) => {
    emitEvent('job:travel_started', { jobId, vehicleId }, 'OperationsContext');
  }, [emitEvent]);
  
  const checkInToJob = useCallback((jobId: string, location?: { lat: number; lng: number }) => {
    emitEvent('job:checked_in', { jobId, location }, 'OperationsContext');
    // Also emit on workerEventBus for cross-module T&A integration
    try {
      workerEvents.emit('worker:job_checked_in', {
        user_id: userId,
        job_id: jobId,
        location,
        timestamp: Date.now()
      });
    } catch { /* non-critical */ }
  }, [emitEvent, userId]);
  
  const checkOutFromJob = useCallback((jobId: string) => {
    emitEvent('job:checked_out', { jobId }, 'OperationsContext');
    // Also emit on workerEventBus for cross-module T&A integration
    try {
      workerEvents.emit('worker:job_checked_out', {
        user_id: userId,
        job_id: jobId,
        timestamp: Date.now()
      });
    } catch { /* non-critical */ }
  }, [emitEvent, userId]);
  
  // ============================================================================
  // Route Dispatch
  // ============================================================================
  
  const dispatchRoute = useCallback((route: DispatchedRoute) => {
    setDispatchedRoutes(prev => {
      // Replace existing route for this technician or add new
      const existing = prev.findIndex(r => r.technician_id === route.technician_id);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = route;
        return updated;
      }
      return [...prev, route];
    });
    
    emitEvent('route:dispatched', { ...route }, 'OperationsContext');
  }, [emitEvent]);
  
  const getDispatchedRouteForUser = useCallback((userId: number): DispatchedRoute | null => {
    return dispatchedRoutes.find(r => r.technician_id === userId) || null;
  }, [dispatchedRoutes]);
  
  // ============================================================================
  // Vehicle Actions
  // ============================================================================
  
  const selectVehicle = useCallback((vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
    emitEvent('vehicle:selected', { vehicleId }, 'OperationsContext');
  }, [emitEvent]);
  
  const getVehicleById = useCallback((vehicleId: string): FleetVehicle | null => {
    return vehicles.find(v => v.id === vehicleId) || null;
  }, [vehicles]);
  
  // ============================================================================
  // Effects
  // ============================================================================
  
  useEffect(() => {
    if (isAuthenticated && user && !isSessionLocked) {
      void refreshAll();
    }
  }, [isAuthenticated, user, isSessionLocked, refreshAll]);
  
  // Auto-refresh locations every 2 minutes (reduced from 30s to save battery on mobile)
  useEffect(() => {
    if (!isAuthenticated || isSessionLocked) return;

    const interval = setInterval(refreshLocations, 120000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isSessionLocked, refreshLocations]);
  
  // ============================================================================
  // Context Value
  // ============================================================================
  
  const value = useMemo<OperationsContextType>(() => ({
    // State
    jobs,
    todayAppointments,
    vehicles,
    technicianLocations,
    dispatchedRoutes,
    
    // Loading states
    isLoadingJobs,
    isLoadingVehicles,
    isLoadingLocations,
    
    // WebSocket
    isConnected,
    
    // Actions
    refreshJobs,
    refreshVehicles,
    refreshLocations,
    refreshAll,
    
    // Job actions
    updateJobStatus,
    startTravelToJob,
    checkInToJob,
    checkOutFromJob,
    
    // Route dispatch
    dispatchRoute,
    getDispatchedRouteForUser,
    
    // Vehicle actions
    selectVehicle,
    selectedVehicleId,
    getVehicleById,
    
    // Events
    emitEvent,
    onEvent,
    
    // Timestamps
    lastJobsRefresh,
    lastVehiclesRefresh,
    lastLocationsRefresh,

    // Solution/team scoping
    activeSolutionKey,
    activeSolutionTeam,
    userSolutionTeams,
    setActiveSolution,
    isMultiSolution,
    isLoadingSolutions,
    refreshSolutionTeams,
  }), [
    jobs, todayAppointments, vehicles, technicianLocations, dispatchedRoutes,
    isLoadingJobs, isLoadingVehicles, isLoadingLocations, isConnected,
    refreshJobs, refreshVehicles, refreshLocations, refreshAll,
    updateJobStatus, startTravelToJob, checkInToJob, checkOutFromJob,
    dispatchRoute, getDispatchedRouteForUser,
    selectVehicle, selectedVehicleId, getVehicleById,
    emitEvent, onEvent,
    lastJobsRefresh, lastVehiclesRefresh, lastLocationsRefresh,
    activeSolutionKey, activeSolutionTeam, userSolutionTeams,
    setActiveSolution, isMultiSolution, isLoadingSolutions, refreshSolutionTeams
  ]);
  
  return (
    <OperationsContext.Provider value={value}>
      {children}
    </OperationsContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useOperations() {
  const context = useContext(OperationsContext);
  if (context === undefined) {
    throw new Error('useOperations must be used within an OperationsProvider');
  }
  return context;
}

export function useOptionalOperations() {
  return useContext(OperationsContext);
}

// Export types for use in other components
export type { OperationsContextType, EventCallback };
